import React, { useState } from 'react';
import { School } from '../../types/school';
import Modal from '../../Common/Components/Modal';
import { toast } from 'react-toastify';

interface AddSchoolDialogProps {
  onClose: () => void;
  onSave: (school: School) => Promise<void>;
  show: boolean;
}

const AddSchoolDialog: React.FC<AddSchoolDialogProps> = ({
  onClose,
  onSave,
  show
}) => {
  const [schoolName, setSchoolName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSave = async () => {
    if (!schoolName.trim()) {
      toast.error('Please enter a school name');
      return;
    }

    setIsLoading(true);
    try {
      const school: School = {
        id: crypto.randomUUID(),
        name: schoolName.trim(),
        createdAt: new Date(),
      };

      await onSave(school);
      onClose();

      setSchoolName('');
    } catch (error) {
      console.error('Error saving school:', error);
      toast.error('Failed to save school. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
      setSchoolName('');
    }
  };

  return (
    <Modal show={show} onHide={handleClose} id="addSchoolModal" modal-center="true"
      className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
      dialogClassName="w-[calc(100vw-2rem)] mx-4 md:mx-0 md:w-[35rem] bg-white shadow rounded-md dark:bg-zink-600 flex flex-col h-auto">
      <Modal.Header className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500"
        closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500 dark:text-zink-200 dark:hover:text-red-500">
        <Modal.Title className="text-16">Add New School</Modal.Title>
      </Modal.Header>
      <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">School Name *</label>
            <input
              type="text"
              value={schoolName}
              onChange={(e) => setSchoolName(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-custom-500 dark:bg-zink-700 dark:border-zink-500"
              placeholder="Enter school name"
              autoFocus
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="flex items-center justify-between p-4 mt-auto border-t border-slate-200 dark:border-zink-500">
        <button
          onClick={handleClose}
          className="px-4 py-2 text-sm bg-slate-100 hover:bg-slate-200 dark:bg-zink-600 dark:hover:bg-zink-500 rounded-md transition-colors"
          disabled={isLoading}
        >
          Close
        </button>
        <button
          onClick={handleSave}
          disabled={isLoading}
          className="px-4 py-2 text-sm bg-custom-500 text-white hover:bg-custom-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isLoading && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          )}
          Add School
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddSchoolDialog;
